import { DocumentType, Media, UploadType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import {
    customerMediaSelector,
    internalMediaSelector,
    removeUploadedFile,
    updateUploadedFileTitle,
    uploadFile,
} from '@synergy/data-access';

import { PaloAttachments } from '../../../../common/components/palo/palo-attachments';
import { noticeUploaded } from '../../../../common/utils/upload-notice';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface PaloInvoiceAttachmentsProps {
    /** The recipient's side closes once the invoice is out. */
    recipientReadOnly?: boolean;
}

/** The files of an invoice, for the dock's "Anhänge" tab. */
export const PaloInvoiceAttachments = ({ recipientReadOnly }: PaloInvoiceAttachmentsProps): ReactElement => {
    const dispatch = useThunkDispatch();
    const customer = useAppSelector(customerMediaSelector);
    const internal = useAppSelector(internalMediaSelector);
    const downloadBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const documentId = useAppSelector(state => state.invoiceDetail.overview.stepData.values.id);

    const drop =
        (existing: Media[], type: UploadType) =>
        (files: File[], titles: string[] = []) => {
            const deduplicated = files
                .map((file, index) => ({ file, title: titles[index] }))
                .filter(
                    ({ file }) => !existing.some(media => media.originalName === file.name && media.size === file.size)
                );

            if (documentId && deduplicated.length > 0) {
                void noticeUploaded(
                    dispatch,
                    deduplicated.map(({ file, title }) =>
                        dispatch(uploadFile(DocumentType.Invoice, documentId, file, type, title))
                    )
                );
            }
        };

    const remove = (media: Media) => {
        if (documentId) {
            dispatch(removeUploadedFile(DocumentType.Invoice, documentId, media));
        }
    };

    const rename = (media: Media, title: string) => dispatch(updateUploadedFileTitle(media, title));

    const cancel = (media: Media) => media.cancel?.();

    return (
        <PaloAttachments
            groups={[
                {
                    key: 'customer',
                    label: <FormattedMessage id="app.attachments.group.customer" />,
                    files: customer,
                    downloadBaseUrl,
                    onDrop: recipientReadOnly || !documentId ? undefined : drop(customer, UploadType.Customer),
                    onRemove: recipientReadOnly ? undefined : remove,
                    onRename: recipientReadOnly ? undefined : rename,
                    onCancel: cancel,
                    readOnly: recipientReadOnly,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
                {
                    key: 'internal',
                    label: <FormattedMessage id="app.attachments.group.internal" />,
                    files: internal,
                    downloadBaseUrl,
                    onDrop: documentId ? drop(internal, UploadType.Internal) : undefined,
                    onRemove: remove,
                    onRename: rename,
                    onCancel: cancel,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
            ]}
        />
    );
};
